/**
 * Types d'erreurs personnalisés pour le système de booking
 * Fournit une gestion d'erreurs typée et structurée
 */

/**
 * Codes d'erreur pour le système de booking
 */
export type BookingErrorCode =
  | 'ALREADY_CANCELLED'
  | 'BOOKING_NOT_FOUND'
  | 'INVALID_DATE'
  | 'INVALID_SLOT'
  | 'MISSING_CONFIG'
  | 'VALIDATION_ERROR'
  | 'CAL_API_ERROR'
  | 'STAFF_ASSIGNMENT_ERROR'
  | 'DATABASE_ERROR'
  | 'UNKNOWN_ERROR';

/**
 * Erreur personnalisée pour les opérations de booking
 * 
 * @example
 * throw new BookingError(
 *   'Booking already cancelled',
 *   'ALREADY_CANCELLED',
 *   { bookingUid: '123' }
 * );
 */
export class BookingError extends Error {
  public readonly code: BookingErrorCode;
  public readonly details?: any;
  public readonly timestamp: Date;

  constructor(
    message: string,
    code: BookingErrorCode = 'UNKNOWN_ERROR',
    details?: any
  ) {
    super(message);
    this.name = 'BookingError';
    this.code = code;
    this.details = details;
    this.timestamp = new Date();

    // Maintient la stack trace correcte en cas d'héritage
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, BookingError);
    }
  }

  /**
   * Convertit l'erreur en objet JSON sérialisable
   */
  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      details: this.details,
      timestamp: this.timestamp.toISOString(),
    };
  }

  /**
   * Crée une BookingError depuis une erreur inconnue
   */
  static fromUnknown(error: unknown, code: BookingErrorCode = 'UNKNOWN_ERROR'): BookingError {
    if (error instanceof BookingError) {
      return error;
    }

    if (error instanceof Error) {
      return new BookingError(error.message, code, { originalError: error.name });
    }

    return new BookingError(
      String(error),
      code,
      { originalError: error }
    );
  }
}
